import * as rc from "../rive_advanced.mjs";

export interface TouchInteractionsParams {
  canvas: HTMLCanvasElement | OffscreenCanvas;
  artboard: rc.Artboard;
  stateMachines: rc.StateMachineInstance[];
  renderer: rc.Renderer;
  rive: rc.RiveCanvas;
  fit: rc.Fit;
  alignment: rc.Alignment;
  isTouchScrollEnabled?: boolean;
  dispatchPointerExit?: boolean;
  enableMultiTouch?: boolean;
  layoutScaleFactor?: number;
}

interface ClientCoordinates {
  clientX: number;
  clientY: number;
  identifier: number;
  transformedX?: number | undefined;
  transformedY?: number | undefined;
}

/**
 * Extracts ClientCoordinates from a TouchList, respecting multi-touch vs.
 * single-touch mode. In single-touch mode, only the touch matching
 * primaryTouchId is returned (or the first touch when primaryTouchId is null).
 */
const getTouchCoordinates = (
  changedTouches: TouchList,
  enableMultiTouch: boolean,
  primaryTouchId: number | null,
): ClientCoordinates[] => {
  const coordinates: ClientCoordinates[] = [];
  if (enableMultiTouch) {
    for (let i = 0; i < changedTouches.length; i++) {
      const touch = changedTouches[i];
      coordinates.push({
        clientX: touch.clientX,
        clientY: touch.clientY,
        identifier: touch.identifier,
      });
    }
  } else {
    // In "single-touch mode", only track the primary finger identified at touchstart.
    // Search changedTouches for the touch matching the recorded primary touch identifier, or (on initial touchstart)
    // take the first available touch identifier.
    let primaryTouch: Touch | null =
      primaryTouchId !== null
        ? Array.from(changedTouches).find(
            (t) => t.identifier === primaryTouchId,
          ) ?? null
        : changedTouches[0];
    if (primaryTouch) {
      coordinates.push({
        clientX: primaryTouch.clientX,
        clientY: primaryTouch.clientY,
        identifier: primaryTouch.identifier,
      });
    }
  }
  return coordinates;
};

/**
 * Returns the clientX and clientY properties from touch or mouse events. Also
 * calls preventDefault() on the event if it is a touchstart or touchmove to prevent
 * scrolling the page on mobile devices
 * @param event - Either a TouchEvent or a MouseEvent
 * @param isTouchScrollEnabled - Whether touch scrolling is enabled
 * @param enableMultiTouch - Whether to process multiple simultaneous touches
 * @param primaryTouchId - When working with single touches, only process the touch
 *   with this identifier. Pass null to accept any touch (used during touchstart to
 *   capture the first finger down).
 * @returns - Coordinates of the clientX and clientY properties from the touch/mouse event
 */
const getClientCoordinates = (
  event: MouseEvent | TouchEvent,
  isTouchScrollEnabled: boolean,
  enableMultiTouch: boolean,
  primaryTouchId: number | null,
): ClientCoordinates[] => {
  const touchEvent = event as TouchEvent;
  if (touchEvent.changedTouches?.length) {
    // This flag, if false, prevents touch events on the canvas default behavior
    // which may prevent scrolling if a drag motion on the canvas is performed
    if (!isTouchScrollEnabled && ["touchstart", "touchmove"].includes(event.type)) {
      event.preventDefault();
    }
    return getTouchCoordinates(touchEvent.changedTouches, enableMultiTouch, primaryTouchId);
  }
  return [
    {
      clientX: (event as MouseEvent).clientX,
      clientY: (event as MouseEvent).clientY,
      identifier: 0,
    },
  ];
};

/**
 * Registers mouse move/up/down callback handlers on the canvas to send meaningful coordinates to
 * the state machine pointer move/up/down functions based on cursor interaction
 */
export const registerTouchInteractions = ({
  canvas,
  artboard,
  stateMachines = [],
  renderer,
  rive,
  fit,
  alignment,
  isTouchScrollEnabled = false,
  dispatchPointerExit = true,
  enableMultiTouch = false,
  layoutScaleFactor = 1.0,
}: TouchInteractionsParams) => {
  if (
    !canvas ||
    !stateMachines.length ||
    !renderer ||
    !rive ||
    !artboard ||
    typeof window === "undefined"
  ) {
    return null;
  }
  /**
   * After a touchend event, some browsers may fire synthetic mouse events
   * (mouseover, mousedown, mousemove, mouseup) if the touch interaction did not cause
   * any default action (such as scrolling).
   *
   * This is done to simulate the behavior of a mouse for applications that do not support
   * touch events.
   *
   * We're keeping track of the previous event to not send the synthetic mouse events if the
   * touch event was a click (touchstart -> touchend).
   *
   * This is only needed when `isTouchScrollEnabled` is false
   * When true, `preventDefault()` is called which prevents this behaviour.
   **/
  let _prevEventType: string | null = null;
  let _syntheticEventsActive = false;

  /**
   * When enableMultiTouch is false ("single-touch mode"), we track the identifier of the first finger that touched down.
   * All subsequent touch events are filtered to this identifier so that a second finger
   * moving cannot displace the tracked pointer position.
   * Reset to null when the primary finger lifts (or touchcancel is called)
   */
  let _primaryTouchId: number | null = null;

  const processEventCallback = (event: MouseEvent | TouchEvent) => {
    // Exit early out of all synthetic mouse events
    // https://stackoverflow.com/questions/9656990/how-to-prevent-simulated-mouse-events-in-mobile-browsers
    // https://stackoverflow.com/questions/25572070/javascript-touchend-versus-click-dilemma
    if (_syntheticEventsActive && event instanceof MouseEvent) {
      // Synthetic event finished
      if (event.type == "mouseup") {
        _syntheticEventsActive = false;
      }

      return;
    }

    // Test if it's a "touch click". This could cause the browser to send
    // synthetic mouse events.
    _syntheticEventsActive =
      isTouchScrollEnabled &&
      event.type === "touchend" &&
      _prevEventType === "touchstart";

    _prevEventType = event.type;

    const boundingRect = (
      event.currentTarget as HTMLCanvasElement
    ).getBoundingClientRect();

    // On touchstart in single-touch mode, record the first new finger as the primary
    // touch if we aren't already tracking one.
    if (!enableMultiTouch && event.type === "touchstart" && _primaryTouchId === null) {
      const firstTouch = (event as TouchEvent).changedTouches?.[0];
      if (firstTouch) {
        _primaryTouchId = firstTouch.identifier;
      }
    }

    const coordinateSets = getClientCoordinates(
      event,
      isTouchScrollEnabled,
      enableMultiTouch,
      enableMultiTouch ? null : _primaryTouchId,
    );
    const forwardMatrix = rive.computeAlignment(
      fit,
      alignment,
      {
        minX: 0,
        minY: 0,
        maxX: boundingRect.width,
        maxY: boundingRect.height,
      },
      artboard.bounds,
      layoutScaleFactor,
    );
    const invertedMatrix = new rive.Mat2D();
    forwardMatrix.invert(invertedMatrix);

    coordinateSets.forEach((coordinateSet) => {
      const clientX = coordinateSet.clientX;
      const clientY = coordinateSet.clientY;
      if (!clientX && !clientY) {
        return;
      }
      const canvasX = clientX - boundingRect.left;
      const canvasY = clientY - boundingRect.top;
      const canvasCoordinatesVector = new rive.Vec2D(canvasX, canvasY);
      const transformedVector = rive.mapXY(
        invertedMatrix,
        canvasCoordinatesVector,
      );
      const transformedX = transformedVector.x();
      const transformedY = transformedVector.y();
      coordinateSet.transformedX = transformedX;
      coordinateSet.transformedY = transformedY;
      transformedVector.delete();
      canvasCoordinatesVector.delete();
    });

    invertedMatrix.delete();
    forwardMatrix.delete();

    switch (event.type) {
      /**
       * There's a 2px buffer for a hitRadius when translating the pointer coordinates
       * down to the state machine. In cases where the hitbox is about that much away
       * from the Artboard border, we don't have exact precision on determining pointer
       * exit. We're therefore adding to the translated coordinates on mouseout of a canvas
       * to ensure that we report the mouse has truly exited the hitarea.
       * https://github.com/rive-app/rive-cpp/blob/master/src/animation/state_machine_instance.cpp#L336
       *
       */
      case "mouseout":
        for (const stateMachine of stateMachines) {
          if (dispatchPointerExit) {
            coordinateSets.forEach((coordinateSet) => {
              stateMachine.pointerExit(
                coordinateSet.transformedX,
                coordinateSet.transformedY,
                coordinateSet.identifier,
              );
            });
          } else {
            coordinateSets.forEach((coordinateSet) => {
              stateMachine.pointerMove(
                coordinateSet.transformedX,
                coordinateSet.transformedY,
                coordinateSet.identifier,
              );
            });
          }
        }
        break;

      // Pointer moving/hovering on the canvas
      case "touchmove":
      case "mouseover":
      case "mousemove": {
        for (const stateMachine of stateMachines) {
          coordinateSets.forEach((coordinateSet) => {
            stateMachine.pointerMove(
              coordinateSet.transformedX,
              coordinateSet.transformedY,
              coordinateSet.identifier,
            );
          });
        }
        break;
      }
      // Pointer click initiated but not released yet on the canvas
      case "touchstart":
      case "mousedown": {
        for (const stateMachine of stateMachines) {
          coordinateSets.forEach((coordinateSet) => {
            stateMachine.pointerDown(
              coordinateSet.transformedX,
              coordinateSet.transformedY,
              coordinateSet.identifier,
            );
          });
        }
        break;
      }
      // Pointer click released on the canvas
      case "touchend": {
        for (const stateMachine of stateMachines) {
          coordinateSets.forEach((coordinateSet) => {
            stateMachine.pointerUp(
              coordinateSet.transformedX,
              coordinateSet.transformedY,
              coordinateSet.identifier,
            );
            stateMachine.pointerExit(
              coordinateSet.transformedX,
              coordinateSet.transformedY,
              coordinateSet.identifier,
            );
          });
        }
        // Release the primary touch lock once that finger lifts so the next
        // touchstart can claim a new primary finger.
        if (
          !enableMultiTouch &&
          coordinateSets.some((c) => c.identifier === _primaryTouchId)
        ) {
          _primaryTouchId = null;
        }
        break;
      }
      case "mouseup": {
        for (const stateMachine of stateMachines) {
          coordinateSets.forEach((coordinateSet) => {
            stateMachine.pointerUp(
              coordinateSet.transformedX,
              coordinateSet.transformedY,
              coordinateSet.identifier,
            );
          });
        }
        break;
      }
      default:
    }
  };

  const touchCancelCallback = () => {
    _primaryTouchId = null;
  };

  const callback = processEventCallback.bind(this);
  canvas.addEventListener("mouseover", callback);
  canvas.addEventListener("mouseout", callback);
  canvas.addEventListener("mousemove", callback);
  canvas.addEventListener("mousedown", callback);
  canvas.addEventListener("mouseup", callback);
  canvas.addEventListener("touchmove", callback, {
    passive: isTouchScrollEnabled,
  });
  canvas.addEventListener("touchstart", callback, {
    passive: isTouchScrollEnabled,
  });
  canvas.addEventListener("touchend", callback);
  canvas.addEventListener("touchcancel", touchCancelCallback);
  return () => {
    canvas.removeEventListener("mouseover", callback);
    canvas.removeEventListener("mouseout", callback);
    canvas.removeEventListener("mousemove", callback);
    canvas.removeEventListener("mousedown", callback);
    canvas.removeEventListener("mouseup", callback);
    canvas.removeEventListener("touchmove", callback);
    canvas.removeEventListener("touchstart", callback);
    canvas.removeEventListener("touchend", callback);
    canvas.removeEventListener("touchcancel", touchCancelCallback);
  };
};
